/**
 * 模拟数据工具
 * 为开发和演示提供假数据
 */

import dayjs from 'dayjs';

/**
 * 生成指定范围的随机数
 */
export const randomBetween = (min: number, max: number): number => {
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

/**
 * 从数组中随机选择一个元素
 */
export const randomChoice = <T>(array: T[]): T => {
  return array[randomBetween(0, array.length - 1)];
};

/**
 * 生成随机颜色
 */
export const randomColor = (): string => {
  const colors = [
    '#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', 
    '#fa541c', '#13c2c2', '#eb2f96', '#2f54eb', '#a0d911'
  ];
  return colors[randomBetween(0, colors.length - 1)];
};

/**
 * 生成时间序列数据
 */
export const generateTimeSeriesData = (
  days: number = 30,
  baseValue: number = 100,
  variance: number = 20
): Array<{ x: string; y: number; date: string }> => {
  const data = [];
  for (let i = days - 1; i >= 0; i--) {
    const date = dayjs().subtract(i, 'day');
    const value = baseValue + randomBetween(-variance, variance);
    data.push({
      x: date.format('MM-DD'),
      y: Math.max(0, value),
      date: date.format('YYYY-MM-DD'),
    });
  }
  return data;
};

/**
 * 生成双轴图数据
 */
export const generateDualAxesData = (
  days: number = 30
): Array<{ x: string; y1: number; y2: number }> => {
  const data = [];
  for (let i = days - 1; i >= 0; i--) {
    const date = dayjs().subtract(i, 'day');
    data.push({
      x: date.format('MM-DD'),
      y1: randomBetween(50, 200),
      y2: randomBetween(1000, 5000),
    });
  }
  return data;
};

/**
 * 生成柱状图数据
 */
export const generateColumnData = (
  categories: string[],
  baseValue: number = 100,
  variance: number = 50
): Array<{ x: string; y: number; type: string }> => {
  return categories.map(category => ({
    x: category,
    y: baseValue + randomBetween(-variance, variance),
    type: category,
  }));
};

/**
 * 生成饼图数据
 */
export const generatePieData = (
  categories: string[]
): Array<{ type: string; value: number; name: string }> => {
  const total = 100;
  let remaining = total;
  
  return categories.map((category, index) => {
    const isLast = index === categories.length - 1;
    const value = isLast ? remaining : randomBetween(5, Math.floor(remaining / 2));
    remaining -= value;
    
    return {
      type: category,
      value,
      name: category,
    };
  });
};

/**
 * 生成分组柱状图数据
 */
export const generateGroupedColumnData = (
  categories: string[],
  groups: string[]
): Array<{ x: string; y: number; type: string }> => {
  const data: Array<{ x: string; y: number; type: string }> = [];

  categories.forEach(category => {
    groups.forEach(group => {
      data.push({
        x: category,
        y: randomBetween(20, 100),
        type: group,
      });
    });
  });

  return data;
};

/**
 * 工作台图表数据
 */
export const getWorkplaceChartData = () => {
  // 订单趋势数据（最近30天）
  const orderTrendData = generateTimeSeriesData(30, 120, 30);
  
  // 销售额趋势数据
  const revenueTrendData = generateTimeSeriesData(30, 5000, 1000);
  
  // 用户增长数据
  const userGrowthData = generateTimeSeriesData(30, 50, 15);
  
  // 产品分类销量数据
  const categoryData = generateColumnData([
    '李宁篮球鞋', '李宁跑步鞋', '李宁运动服', '李宁羽毛球装备', '李宁休闲产品'
  ], 80, 40);
  
  // 地区销售分布
  const regionData = generatePieData([
    '华北', '华东', '华南', '华中', '西南', '西北', '东北'
  ]);
  
  // 销售渠道对比
  const channelData = generateGroupedColumnData(
    ['李宁官网', '李宁专卖店', '李宁授权店', '电商平台'],
    ['本月', '上月']
  );
  
  // 销售额和订单量双轴图
  const salesAndOrderData = generateDualAxesData(30);
  
  return {
    orderTrend: orderTrendData,
    revenueTrend: revenueTrendData,
    userGrowth: userGrowthData,
    categoryDistribution: categoryData,
    regionDistribution: regionData,
    channelComparison: channelData,
    salesAndOrder: salesAndOrderData,
  };
};

/**
 * 获取实时数据（模拟）
 */
export const getRealTimeData = () => {
  return {
    todayOrders: randomBetween(50, 200),
    todayRevenue: randomBetween(10000, 50000),
    todayUsers: randomBetween(100, 500),
    onlineUsers: randomBetween(20, 100),
    conversionRate: (randomBetween(15, 35) / 10).toFixed(1),
    avgOrderValue: randomBetween(200, 800),
  };
};

/**
 * 获取系统状态数据
 */
export const getSystemStatusData = () => {
  return {
    cpuUsage: randomBetween(10, 90) / 100,
    memoryUsage: randomBetween(20, 80) / 100,
    diskUsage: randomBetween(30, 70) / 100,
    networkSpeed: randomBetween(50, 100),
    responseTime: randomBetween(50, 200),
    errorRate: randomBetween(0, 5) / 100,
  };
};

/**
 * 生成数据分析页面数据
 */
export const generateAnalyticsData = (timeRange: string) => {
  const days = timeRange === '7days' ? 7 : timeRange === '30days' ? 30 : timeRange === '90days' ? 90 : 365;
  
  // 访问趋势数据
  const visitTrend = generateTimeSeriesData(days, 1200, 300).map(item => ({
    date: item.date,
    visits: item.y
  }));
  
  // 销售趋势数据
  const salesTrend = generateTimeSeriesData(days, 8500, 2000).map(item => ({
    date: item.date,
    sales: item.y
  }));
  
  // 地域分布数据
  const regionData = generatePieData(['北京', '上海', '广州', '深圳', '杭州', '其他']).map(item => ({
    region: item.type,
    value: item.value
  }));
  
  // 热销李宁商品数据
  const topProducts = Array.from({ length: 10 }, (_, index) => {
    const liningProducts = [
      '李宁韦德之道10代', '李宁超轻19代', '李宁云四代', '李宁突袭TD',
      '李宁风动9000i', '李宁运动T恤', '李宁篮球短裤', '李宁夹克外套',
      '李宁双肩背包', '李宁专业羽毛球'
    ];
    return {
      key: index + 1,
      rank: index + 1,
      name: liningProducts[index] || `李宁产品${index + 1}`,
      sku: `LN${String(index + 1).padStart(4, '0')}`,
      sales: randomBetween(500, 3000),
      revenue: randomBetween(50000, 300000),
      growth: randomBetween(-20, 50)
    };
  });
  
  // 李宁产品分类销售数据
  const categoryData = generatePieData(['李宁篮球鞋', '李宁跑步鞋', '李宁运动服装', '李宁羽毛球装备', '李宁休闲产品', '李宁配件']).map(item => ({
    category: item.type,
    value: item.value
  }));
  
  // 销售时段热力图数据
  const timeHeatmap = [];
  const days_week = ['周一', '周二', '周三', '周四', '周五', '周六', '周日'];
  for (let day = 0; day < 7; day++) {
    for (let hour = 0; hour < 24; hour++) {
      timeHeatmap.push({
        day: days_week[day],
        hour: hour,
        sales: randomBetween(10, 100)
      });
    }
  }
  
  // 用户画像数据
  const userProfile = [
    { dimension: '年龄', value: randomBetween(60, 90), type: '18-25岁' },
    { dimension: '性别', value: randomBetween(40, 80), type: '男性' },
    { dimension: '地域', value: randomBetween(50, 90), type: '一线城市' },
    { dimension: '消费力', value: randomBetween(30, 70), type: '中高消费' },
    { dimension: '活跃度', value: randomBetween(60, 95), type: '高活跃' },
    { dimension: '忠诚度', value: randomBetween(40, 80), type: '较忠诚' }
  ];
  
  // 用户价值分布数据
  const userValue = Array.from({ length: 50 }, () => ({
    recency: randomBetween(1, 100),
    frequency: randomBetween(1, 50),
    monetary: randomBetween(100, 5000)
  }));
  
  // 留存分析数据
  const retentionData = [];
  const cohorts = ['第1周', '第2周', '第3周', '第4周'];
  for (let cohort = 0; cohort < cohorts.length; cohort++) {
    for (let week = 0; week < 8; week++) {
      retentionData.push({
        cohort: cohorts[cohort],
        week: `第${week + 1}周`,
        retention: Math.max(0, 100 - week * 15 - cohort * 5 + randomBetween(-10, 10))
      });
    }
  }
  
  // 渠道分析数据（李宁销售渠道）
  const channelAnalysis = [
    { stage: '展现', value: 10000, channel: '李宁官网' },
    { stage: '点击', value: 2000, channel: '李宁官网' },
    { stage: '访问', value: 1500, channel: '李宁官网' },
    { stage: '注册', value: 300, channel: '李宁官网' },
    { stage: '购买', value: 150, channel: '李宁官网' },
    { stage: '展现', value: 8000, channel: '李宁专卖店' },
    { stage: '点击', value: 2400, channel: '李宁专卖店' },
    { stage: '访问', value: 1800, channel: '李宁专卖店' },
    { stage: '注册', value: 450, channel: '李宁专卖店' },
    { stage: '购买', value: 200, channel: '李宁专卖店' }
  ];
  
  // 李宁渠道效果对比
  const channelCompare = [
    { channel: '李宁官网', metric: '点击率', value: 20 },
    { channel: '李宁官网', metric: '转化率', value: 7.5 },
    { channel: '李宁官网', metric: 'ROI', value: 3.2 },
    { channel: '李宁专卖店', metric: '点击率', value: 30 },
    { channel: '李宁专卖店', metric: '转化率', value: 8.3 },
    { channel: '李宁专卖店', metric: 'ROI', value: 2.8 },
    { channel: '天猫旗舰店', metric: '点击率', value: 15 },
    { channel: '天猫旗舰店', metric: '转化率', value: 12 },
    { channel: '天猫旗舰店', metric: 'ROI', value: 4.5 }
  ];
  
  // 李宁渠道详细数据
  const channelDetails = [
    { channel: '李宁官网', visits: 15000, conversion: 7.5, roi: 3.2, cost: 25000 },
    { channel: '李宁专卖店', visits: 18000, conversion: 8.3, roi: 2.8, cost: 30000 },
    { channel: '天猫旗舰店', visits: 12000, conversion: 12, roi: 4.5, cost: 15000 },
    { channel: '京东旗舰店', visits: 8000, conversion: 6.2, roi: 2.1, cost: 10000 },
    { channel: '李宁授权店', visits: 6000, conversion: 4.8, roi: 1.8, cost: 12000 }
  ];
  
  // 销售额与订单量对比数据
  const salesOrderData = Array.from({ length: 12 }, (_, index) => ({
    month: `${index + 1}月`,
    sales: randomBetween(500, 1200),
    orders: randomBetween(1000, 2500)
  }));
  
  return {
    totalVisits: randomBetween(80000, 120000),
    totalRevenue: randomBetween(500000, 800000),
    conversionRate: (randomBetween(60, 90) / 10).toFixed(1),
    activeUsers: randomBetween(5000, 8000),
    
    visitTrend,
    salesTrend,
    regionData,
    topProducts,
    categoryData,
    timeHeatmap,
    userProfile,
    userValue,
    retentionData,
    channelAnalysis,
    channelCompare,
    channelDetails,
    salesOrderData
  };
};

export default {
  randomBetween,
  randomColor,
  generateTimeSeriesData,
  generateDualAxesData,
  generateColumnData,
  generatePieData,
  generateGroupedColumnData,
  getWorkplaceChartData,
  getRealTimeData,
  getSystemStatusData,
  generateAnalyticsData,
};